import type {
  CollectionName, CollectionTypes, ErrorCode, Question, RawBlankReply, RawCheckReply,
  RawReadReply, ReadItem, Submission, User, Verdict, Worksheet, WorksheetForAI,
} from "../shared/types.js";
import { convexHull, orderCorners, quadArea, solveHomography, type Pt } from "./geometry.js";
import { mountSpotlight } from "./spotlight.js";
import { mountPeeker, peeker } from "./peeker.js";
import { mountRoller, ROLLER } from "./roller.js";
import { mountMonsters } from "./monsters.js";
import { mountQuips, quipLine } from "./quips.js";
import { mountZoom, newZoom, type ZoomState } from "./zoom.js";
import { asRecord, csvCell, mergeCheck, normalizeExtraction, num, recompute, str, strList, type Extraction } from "./grading.js";

/* ---------------- types ---------------- */
type PageStatus = "loading" | "straighten" | "ready" | "reading" | "review" | "checking" | "done" | "error";
type Tab = "grade" | "sheets" | "results";

/** Fields filled in while straightening a photo. */
interface CropFields {
  corners: Pt[];
  detected?: Pt[] | null;   // null = detection tried and failed
  auto?: boolean;           // straightened from detected corners
  blob?: Blob;
  sendUrl?: string;         // full-quality JPEG sent to Claude
  storedUrl?: string;       // compressed JPEG kept in storage
  thumb?: string;
}
type CropTarget = CropFields & { src: HTMLCanvasElement };


/** One photographed page in the current stack. */
interface Page extends CropFields {
  id: string;
  n: number;
  status: PageStatus;
  src?: HTMLCanvasElement;
  extraction?: Extraction;
  studentName?: string;
  worksheetId?: string | null;
  matchNote?: string;
  readBy?: Tier;            // which monster read it, for the review heading
  sub?: Submission | null;
  ctl?: AbortController;
  error?: string;
  errorCode?: string;
  retry?: PageStatus | null;
}

/** A worksheet being added or edited. */
interface WorksheetDraft extends Worksheet, Partial<CropFields> {
  src?: HTMLCanvasElement;
  stage: "photo" | "crop" | "reading" | "form";
  isNew?: boolean;
  templateUrl?: string;
  proposed?: Question[];        // questions read from a new photo, waiting for the teacher to accept
  error?: string;
  ctl?: AbortController;
  zoom?: ZoomState;             // how far the teacher has zoomed into the blank worksheet, kept across re-renders
}

interface State {
  ready: boolean;
  user: User | null;
  authMode: "login" | "signup" | "student";
  aiReady: boolean;             // signed in, so Claude calls can be made
  downloads: boolean;
  base: DataStore | null;
  worksheets: Worksheet[];
  submissions: Submission[];
  imgCache: Record<string, string>;
  pages: Page[];
  cur: string | null;
  pageCounter: number;
  stackWs: string;              // "auto", "plain" or a worksheet id
  tier: Tier;
  autoStraighten: boolean;
  batch: boolean;
  stopBatch: boolean;
  tab: Tab;
  wsEdit: WorksheetDraft | null;
  resultOpen: string | null;
  resultFilter: string;         // "all", "plain" or a worksheet id
}

/** An error with one of the server's error codes (or "cancelled"/"network"). */
interface CodedError extends Error { code: ErrorCode; serverMessage?: string }
const codeOf = (e: unknown): ErrorCode | undefined => (e as Partial<CodedError> | null)?.code;

/* ---------------- utilities ---------------- */
/** querySelector that assumes the element exists (the markup is ours). */
function $<T extends Element = HTMLElement>(s: string, r: ParentNode = document): T { return r.querySelector(s) as T; }
function $$<T extends Element = HTMLElement>(s: string, r: ParentNode = document): T[] { return Array.from(r.querySelectorAll<T>(s)); }
const ESC: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"']/g, c => ESC[c]);
const newId = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
const fmtNum = (n: number): string => (Math.round(n * 10) / 10).toString();
const fmtDate = (t: number): string => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
/** Value of the input/select/textarea that fired an event. */
const val = (e: Event): string => (e.target as HTMLInputElement).value;
let toastTimer: ReturnType<typeof setTimeout> | undefined;
function toast(msg: string): void {
  const t = $("#toast"); t.textContent = msg; t.hidden = false;
  clearTimeout(toastTimer); toastTimer = setTimeout(() => (t.hidden = true), 3600);
}

/* ---------------- state ---------------- */
const S: State = {
  ready: false, user: null, authMode: "student", aiReady: false, downloads: false, base: null,
  worksheets: [], submissions: [], imgCache: {},
  pages: [], cur: null, pageCounter: 0, stackWs: "auto", tier: "default", autoStraighten: true, batch: false, stopBatch: false,
  tab: "grade", wsEdit: null, resultOpen: null, resultFilter: "all",
};
const wsById = (id: string | null | undefined): Worksheet | null => S.worksheets.find(w => w.id === id) || null;

/* ---------------- storage ---------------- */
async function loadAll(): Promise<void> {
  if (!S.base) return;
  try {
    const [w, s] = await Promise.all([S.base.collection("worksheets").get(), S.base.collection("submissions").get()]);
    S.worksheets = w.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => str(a.title).localeCompare(str(b.title)));
    S.submissions = s.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) => num(b.createdAt) - num(a.createdAt));
  } catch (e) { toast("Saved work couldn't be loaded. Reload to try again."); }
}
const plain = <T,>(o: T): T => JSON.parse(JSON.stringify(o)) as T;
async function putImage(id: string, dataUrl: string): Promise<void> {
  S.imgCache[id] = dataUrl;
  if (S.base) await S.base.collection("images").doc(id).set({ data: dataUrl });
}
async function getImage(id: string | null | undefined): Promise<string | null> {
  if (!id) return null;
  if (S.imgCache[id]) return S.imgCache[id];
  if (!S.base) return null;
  try {
    const d = await S.base.collection("images").doc(id).get();
    const data = d.data();
    if (d.exists && data) { S.imgCache[id] = data.data; return data.data; }
  } catch (e) {}
  return null;
}
async function delImage(id: string | null | undefined): Promise<void> {
  if (!id) return;
  delete S.imgCache[id];
  if (S.base) try { await S.base.collection("images").doc(id).delete(); } catch (e) {}
}
async function saveWorksheetDoc(ws: Worksheet): Promise<void> {
  const i = S.worksheets.findIndex(w => w.id === ws.id);
  if (i >= 0) S.worksheets[i] = ws; else S.worksheets.push(ws);
  S.worksheets.sort((a, b) => str(a.title).localeCompare(str(b.title)));
  if (S.base) await S.base.collection("worksheets").doc(ws.id).set(plain(ws));
}
const SAVE_FAILED = "This result couldn't be saved. It stays on screen until you close the page.";
async function saveSubmission(sub: Submission, page?: Page): Promise<void> {
  try {
    if (page && !sub.imageId && page.storedUrl) { sub.imageId = newId(); await putImage(sub.imageId, page.storedUrl); }
    const i = S.submissions.findIndex(x => x.id === sub.id);
    if (i < 0) S.submissions.unshift(sub);
    if (S.base) await S.base.collection("submissions").doc(sub.id).set(plain(sub));
  } catch (e) { toast(SAVE_FAILED); }
}
// One pending save per result, so editing another result doesn't drop this one's edits.
const pendingSaves = new Map<string, { sub: Submission; timer: ReturnType<typeof setTimeout> }>();
function saveSubSoon(sub: Submission): void {
  clearTimeout(pendingSaves.get(sub.id)?.timer);
  pendingSaves.set(sub.id, { sub, timer: setTimeout(() => { pendingSaves.delete(sub.id); void saveSubmission(sub); }, 700) });
}
/** Send any pending saves now; keepalive lets them finish if the page is closing. */
function flushSaves(): void {
  for (const { sub, timer } of pendingSaves.values()) {
    clearTimeout(timer);
    if (!S.base) continue;
    const body = JSON.stringify(sub);
    // Browsers refuse keepalive bodies over 64 KB, so send big results the normal way.
    if (new TextEncoder().encode(body).length > 60_000) { void saveSubmission(sub); continue; }
    // The page may still be open (e.g. the teacher switched tabs), so say if the save failed.
    fetch(`/api/data/submissions/${encodeURIComponent(sub.id)}`, {
      method: "PUT", keepalive: true, credentials: "same-origin",
      headers: { "Content-Type": "application/json" }, body,
    }).then(res => { if (!res.ok) toast(SAVE_FAILED); }, () => toast(SAVE_FAILED));
  }
  pendingSaves.clear();
}
addEventListener("pagehide", flushSaves);
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushSaves(); });

/* ---------------- image processing ---------------- */
const ctx2d = (c: HTMLCanvasElement): CanvasRenderingContext2D => c.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
function loadImageFile(file: Blob): Promise<HTMLCanvasElement> {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = () => rej(new Error("That file couldn't be opened."));
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const s = Math.min(1, 2400 / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas");
        c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
        ctx2d(c).drawImage(img, 0, 0, c.width, c.height);
        res(c);
      };
      img.onerror = () => rej(new Error("That file isn't an image this browser can open. Try a JPEG or PNG."));
      img.src = fr.result as string;
    };
    fr.readAsDataURL(file);
  });
}
function scaleCanvas(src: HTMLCanvasElement, max: number): HTMLCanvasElement {
  const s = Math.min(1, max / Math.max(src.width, src.height));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(src.width * s)); c.height = Math.max(1, Math.round(src.height * s));
  const x = ctx2d(c); x.imageSmoothingQuality = "high"; x.drawImage(src, 0, 0, c.width, c.height);
  return c;
}
function rotateCanvas(src: HTMLCanvasElement): HTMLCanvasElement {
  const c = document.createElement("canvas"); c.width = src.height; c.height = src.width;
  const x = ctx2d(c); x.translate(c.width, 0); x.rotate(Math.PI / 2); x.drawImage(src, 0, 0);
  return c;
}
const fullCorners = (c: HTMLCanvasElement): Pt[] => [[0, 0], [c.width, 0], [c.width, c.height], [0, c.height]];
function warp(src: HTMLCanvasElement, corners: Pt[]): HTMLCanvasElement {
  const q = orderCorners(corners);
  const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  let W = Math.max(dist(q[0], q[1]), dist(q[3], q[2])), H = Math.max(dist(q[0], q[3]), dist(q[1], q[2]));
  const sc = Math.min(1, 1600 / Math.max(W, H)); W = Math.max(10, Math.round(W * sc)); H = Math.max(10, Math.round(H * sc));
  const h = solveHomography([[0, 0], [W, 0], [W, H], [0, H]], q);
  const sw = src.width, sh = src.height, sd = ctx2d(src).getImageData(0, 0, sw, sh).data;
  const out = document.createElement("canvas"); out.width = W; out.height = H;
  const octx = ctx2d(out), od = octx.createImageData(W, H), o = od.data;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const den = h[6] * x + h[7] * y + 1, sx = (h[0] * x + h[1] * y + h[2]) / den, sy = (h[3] * x + h[4] * y + h[5]) / den;
    const i = (y * W + x) * 4;
    if (!(sx >= 0 && sy >= 0 && sx <= sw - 1 && sy <= sh - 1)) { o[i] = o[i + 1] = o[i + 2] = o[i + 3] = 255; continue; }
    const x0 = sx | 0, y0 = sy | 0, x1 = Math.min(x0 + 1, sw - 1), y1 = Math.min(y0 + 1, sh - 1), fx = sx - x0, fy = sy - y0;
    const a = (y0 * sw + x0) * 4, b = (y0 * sw + x1) * 4, c = (y1 * sw + x0) * 4, d = (y1 * sw + x1) * 4;
    for (let k = 0; k < 3; k++) o[i + k] = (sd[a + k] * (1 - fx) + sd[b + k] * fx) * (1 - fy) + (sd[c + k] * (1 - fx) + sd[d + k] * fx) * fy;
    o[i + 3] = 255;
  }
  octx.putImageData(od, 0, 0);
  return out;
}
function enhance(c: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = ctx2d(c), im = ctx.getImageData(0, 0, c.width, c.height), d = im.data, hist = new Uint32Array(256);
  for (let i = 0; i < d.length; i += 4) hist[(d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0]++;
  const n = d.length / 4; let lo = 0, hi = 255, acc = 0;
  for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc > n * 0.01) { lo = v; break; } }
  acc = 0; for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc > n * 0.06) { hi = v; break; } }
  if (hi - lo < 40) return c;
  const s = 255 / (hi - lo);
  for (let i = 0; i < d.length; i += 4) for (let k = 0; k < 3; k++) d[i + k] = Math.max(0, Math.min(255, (d[i + k] - lo) * s));
  ctx.putImageData(im, 0, 0);
  return c;
}
/* ---------- automatic page detection ----------
   Paper is bright and colourless, so score each pixel by its darkest channel minus a
   saturation penalty, blur, split paper from background with Otsu's threshold, keep the
   largest bright region, take its convex hull, and fit the largest-area quadrilateral. */
function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h), out = new Float32Array(w * h), n = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    let s = 0; for (let x = -r; x <= r; x++) s += src[y * w + Math.min(w - 1, Math.max(0, x))];
    for (let x = 0; x < w; x++) { tmp[y * w + x] = s / n; s += src[y * w + Math.min(w - 1, x + r + 1)] - src[y * w + Math.max(0, x - r)]; }
  }
  for (let x = 0; x < w; x++) {
    let s = 0; for (let y = -r; y <= r; y++) s += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
    for (let y = 0; y < h; y++) { out[y * w + x] = s / n; s += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x]; }
  }
  return out;
}
function detectPage(src: HTMLCanvasElement): Pt[] | null {
  try {
    const c = scaleCanvas(src, 360), w = c.width, h = c.height, k = w / src.width;
    const d = ctx2d(c).getImageData(0, 0, w, h).data;
    let g: Float32Array = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const r = d[i * 4], gg = d[i * 4 + 1], b = d[i * 4 + 2], mn = Math.min(r, gg, b), mx = Math.max(r, gg, b);
      g[i] = Math.max(0, mn - (mx - mn) * 0.6);
    }
    g = boxBlur(g, w, h, 3);
    // Otsu threshold
    const hist = new Float64Array(256); for (let i = 0; i < g.length; i++) hist[Math.min(255, g[i] | 0)]++;
    const N = g.length; let sumAll = 0; for (let v = 0; v < 256; v++) sumAll += v * hist[v];
    let wB = 0, sumB = 0, best = 0, t = 128;
    for (let v = 0; v < 256; v++) {
      wB += hist[v]; if (!wB) continue; const wF = N - wB; if (!wF) break;
      sumB += v * hist[v]; const mB = sumB / wB, mF = (sumAll - sumB) / wF, between = wB * wF * (mB - mF) ** 2;
      if (between > best) { best = between; t = v; }
    }
    if (Math.sqrt(best / (N * N)) < 12) return null; // paper and background too similar
    // largest bright connected region
    const lab = new Int32Array(w * h), stack = new Int32Array(w * h); let next = 0, bestLab = 0, bestSize = 0;
    for (let i = 0; i < w * h; i++) {
      if (lab[i] || g[i] <= t) continue;
      next++; let sp = 0, size = 0; stack[sp++] = i; lab[i] = next;
      while (sp) {
        const j = stack[--sp]; size++; const x = j % w, y = (j / w) | 0;
        if (x > 0 && !lab[j - 1] && g[j - 1] > t) { lab[j - 1] = next; stack[sp++] = j - 1; }
        if (x < w - 1 && !lab[j + 1] && g[j + 1] > t) { lab[j + 1] = next; stack[sp++] = j + 1; }
        if (y > 0 && !lab[j - w] && g[j - w] > t) { lab[j - w] = next; stack[sp++] = j - w; }
        if (y < h - 1 && !lab[j + w] && g[j + w] > t) { lab[j + w] = next; stack[sp++] = j + w; }
      }
      if (size > bestSize) { bestSize = size; bestLab = next; }
    }
    if (bestSize < w * h * 0.12) return null;
    const edge: Pt[] = [];
    for (let y = 0; y < h; y++) {
      let a = -1, b = -1;
      for (let x = 0; x < w; x++) if (lab[y * w + x] === bestLab) { if (a < 0) a = x; b = x; }
      if (a >= 0) { edge.push([a, y]); edge.push([b + 1, y]); }
    }
    const hull = convexHull(edge); if (hull.length < 4) return null;
    const sgn = (p: Pt) => p[0] + p[1], dif = (p: Pt) => p[0] - p[1];
    let q: Pt[] = [hull.reduce((a, b) => (sgn(b) < sgn(a) ? b : a)), hull.reduce((a, b) => (dif(b) > dif(a) ? b : a)),
             hull.reduce((a, b) => (sgn(b) > sgn(a) ? b : a)), hull.reduce((a, b) => (dif(b) < dif(a) ? b : a))];
    for (let it = 0; it < 3; it++) for (let ci = 0; ci < 4; ci++) {
      let bestA = quadArea(q), bestP = q[ci];
      for (const p of hull) { const tq = q.slice(); tq[ci] = p; const a = quadArea(tq); if (a > bestA) { bestA = a; bestP = p; } }
      q[ci] = bestP;
    }
    q = orderCorners(q);
    const area = quadArea(q);
    if (area < w * h * 0.15) return null;
    for (let i = 0; i < 4; i++) { // reject badly skewed shapes
      const p = q[i], a = q[(i + 3) % 4], b = q[(i + 1) % 4];
      const v1 = [a[0] - p[0], a[1] - p[1]], v2 = [b[0] - p[0], b[1] - p[1]];
      const ang = Math.acos((v1[0] * v2[0] + v1[1] * v2[1]) / (Math.hypot(...v1) * Math.hypot(...v2) || 1)) * 180 / Math.PI;
      if (ang < 50 || ang > 130) return null;
    }
    // back to full-size coordinates, nudged outward slightly to undo the blur
    const cx = q.reduce((s, p) => s + p[0], 0) / 4, cy = q.reduce((s, p) => s + p[1], 0) / 4;
    return q.map(([x, y]): Pt => [
      Math.max(0, Math.min(src.width, (cx + (x - cx) * 1.006) / k)),
      Math.max(0, Math.min(src.height, (cy + (y - cy) * 1.006) / k)),
    ]);
  } catch (e) { return null; }
}

const canvasToBlob = (c: HTMLCanvasElement, type = "image/jpeg", q = 0.9): Promise<Blob> =>
  new Promise((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error("Couldn't encode image"))), type, q));
function storedDataUrl(canvas: HTMLCanvasElement): string {
  let max = 1150, q = 0.74;
  for (let i = 0; i < 7; i++) {
    const u = scaleCanvas(canvas, max).toDataURL("image/jpeg", q);
    if (u.length < 235000) return u;
    max = Math.round(max * 0.86); q = Math.max(0.5, q - 0.05);
  }
  return scaleCanvas(canvas, 520).toDataURL("image/jpeg", 0.5);
}
async function processCrop(obj: CropTarget): Promise<void> {
  const out = enhance(warp(obj.src, obj.corners));
  obj.blob = await canvasToBlob(out);
  obj.sendUrl = out.toDataURL("image/jpeg", 0.88);
  obj.storedUrl = storedDataUrl(out);
  obj.thumb = scaleCanvas(out, 160).toDataURL("image/jpeg", 0.6);
}

/* ---------------- corner editor ---------------- */
function mountCrop(el: HTMLElement, obj: CropTarget, hint?: string): void {
  const w = obj.src.width, h = obj.src.height, r = Math.max(w, h) * 0.022, NS = "http://www.w3.org/2000/svg";
  el.innerHTML = `<div class="crop-stage"></div><p class="hint">${esc(hint || "Drag the four dots onto the corners of the paper. This straightens the page and trims off the desk.")}</p>`;
  const stage = $(".crop-stage", el);
  obj.src.className = "crop-canvas"; obj.src.setAttribute("role", "img"); obj.src.setAttribute("aria-label", "Photo of the page");
  stage.appendChild(obj.src);
  const svg = document.createElementNS(NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`); svg.setAttribute("preserveAspectRatio", "none"); svg.setAttribute("class", "crop-svg");
  svg.innerHTML = `<path class="crop-shade" fill-rule="evenodd"></path><polygon class="crop-poly"></polygon>` +
    [0, 1, 2, 3].map(i => `<g data-h="${i}"><circle class="crop-hit" r="${r * 2.4}"></circle><circle class="crop-dot" r="${r * 0.75}"></circle></g>`).join("");
  stage.appendChild(svg);
  const upd = () => {
    const c = obj.corners;
    $(".crop-poly", svg).setAttribute("points", c.map(p => p.join(",")).join(" "));
    $(".crop-shade", svg).setAttribute("d", `M0 0H${w}V${h}H0Z M${c.map(p => p.join(" ")).join(" L")}Z`);
    svg.querySelectorAll("[data-h]").forEach((g, i) => g.querySelectorAll("circle").forEach(ci => { ci.setAttribute("cx", String(c[i][0])); ci.setAttribute("cy", String(c[i][1])); }));
  };
  upd();
  let drag: number | null = null;
  const toSvg = (e: PointerEvent): DOMPoint => { const p = svg.createSVGPoint(); p.x = e.clientX; p.y = e.clientY; return p.matrixTransform((svg.getScreenCTM() as DOMMatrix).inverse()); };
  svg.addEventListener("pointerdown", e => { const g = (e.target as Element).closest<SVGGElement>("[data-h]"); if (!g) return; drag = Number(g.dataset.h); svg.setPointerCapture(e.pointerId); e.preventDefault(); });
  svg.addEventListener("pointermove", e => { if (drag == null) return; const p = toSvg(e); obj.corners[drag] = [Math.max(0, Math.min(w, p.x)), Math.max(0, Math.min(h, p.y))]; upd(); });
  const end = () => (drag = null);
  svg.addEventListener("pointerup", end); svg.addEventListener("pointercancel", end);
}

/* ---------------- server calls ---------------- */
async function api<T>(method: "GET" | "POST" | "PUT" | "DELETE", url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method, signal, credentials: "same-origin",
      headers: body !== undefined ? { "Content-Type": "application/json" } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw Object.assign(new Error("cancelled"), { code: "cancelled" as const });
    throw Object.assign(new Error("network"), { code: "network" as const });
  }
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { code?: ErrorCode; message?: string } } | null)?.error;
    const code: ErrorCode = err?.code || "upstream_error";
    if (code === "session_expired" && S.user) { S.user = null; render(); }
    throw Object.assign(new Error(err?.message || code), { code, serverMessage: err?.message });
  }
  return data as T;
}
interface AiReplies { read: RawReadReply; check: RawCheckReply; blank: RawBlankReply }
const ai = <K extends keyof AiReplies>(kind: K, body: unknown, signal?: AbortSignal): Promise<AiReplies[K]> =>
  api<AiReplies[K]>("POST", `/api/ai/${kind}`, body, signal);
const wsForServer = (w: Worksheet | null): WorksheetForAI | null =>
  w ? { id: w.id, title: w.title, grade: w.grade, notes: w.notes, questions: w.questions || [] } : null;

/** Per-teacher storage on the server, in collections of JSON documents. */
interface DocSnapshot<T> { exists: boolean; data(): T | undefined }
interface DocRef<T> { get(): Promise<DocSnapshot<T>>; set(data: T): Promise<unknown>; delete(): Promise<unknown> }
interface CollectionRef<T> { get(): Promise<{ docs: { id: string; data(): T }[] }>; doc(id: string): DocRef<T> }
interface DataStore { collection<K extends CollectionName>(col: K): CollectionRef<CollectionTypes[K]> }

const storage: DataStore = {
  collection<K extends CollectionName>(col: K): CollectionRef<CollectionTypes[K]> {
    type T = CollectionTypes[K];
    const url = (id: string) => `/api/data/${col}/${encodeURIComponent(id)}`;
    return {
      get: async () => ({ docs: (await api<{ id: string; data: T }[]>("GET", `/api/data/${col}`)).map(d => ({ id: d.id, data: () => d.data })) }),
      doc: (id: string) => ({
        get: async () => {
          try { const d = await api<{ id: string; data: T }>("GET", url(id)); return { exists: true, data: () => d.data }; }
          catch (e) { if (codeOf(e) === "not_found") return { exists: false, data: () => undefined }; throw e; }
        },
        set: (data: T) => api("PUT", url(id), data),
        delete: () => api("DELETE", url(id)),
      }),
    };
  },
};

// The two monsters a teacher picks between to read handwriting. Fiona is the server's standard model, Kraken its careful one;
// worksheets and grading always use the standard model, so Fiona does those.
type Tier = "default" | "complex";
// Each has a static drawing and a looping Lottie animation (Kraken's is Grogg Alex) for while they're working.
// readTime is how long reading a page usually takes; it and GRADE_TIME are estimates for the default models in src/server.ts
// (Haiku for Fiona, Sonnet for Kraken), so revisit them if those change.
const MONSTERS: Record<Tier, { name: string; anim: string; still: string; readTime: string }> = {
  default: { name: "Fiona", anim: "/monsters/fiona.json", still: "/monsters/fiona.svg", readTime: "10 to 30 seconds" },
  complex: { name: "Kraken", anim: "/monsters/kraken.json", still: "/monsters/kraken.svg", readTime: "20 to 60 seconds" },
};
const GRADE_TIME = "5 to 20 seconds";
// Always named in the text beside them. The working one animates: mountMonsters fills it in once it's on the page.
const monsterImg = (tier: Tier, cls = ""): string => cls === "working"
  ? `<span class="monster working" data-monster="${tier}" aria-hidden="true"></span>`
  : `<img class="monster ${cls}" src="${MONSTERS[tier].still}" alt="" draggable="false">`;
// Fiona has the favicon monster for company: while she reads, it rolls back and forth behind her (client/roller.ts),
// and once she's grading, it rolls in and peeks out from behind her, scanning the ground (client/peeker.ts).
const workingMonster = (tier: Tier, task: "reading" | "grading"): string => tier !== "default" ? monsterImg(tier, "working")
  : task === "reading" ? monsterImg(tier, "working") + ROLLER : `<span class="with-peeker">${peeker("ground")}${monsterImg(tier, "working")}</span>`;
const STANDARD = MONSTERS.default.name, CAREFUL = MONSTERS.complex.name; // use these, not the names, in UI text
const readerOf = (s: Submission): Tier | null => s.careful === undefined ? null : s.careful ? "complex" : "default";
const readerName = (s: Submission): string => { const t = readerOf(s); return t ? MONSTERS[t].name : ""; };
const readerTag = (s: Submission): string => { const t = readerOf(s); return t ? `<span class="reader">${monsterImg(t)}${MONSTERS[t].name}</span>` : ""; };
const TAGLINE = "get grading help from your beast teachers";
const HELPER = "teaching assistant"; // what Kraken and Fiona are called together

function errorMessage(code: ErrorCode | undefined): string {
  switch (code) {
    case "rate_limited": return "The monsters are busy or today's limit has been reached. Wait a minute, then try again.";
    case "invalid_json": return "The answer came back in an unexpected shape. Try again.";
    case "reply_too_long": return "The answer was too long and got cut off, so trying again won't help. Try a page with fewer questions.";
    case "image_rejected": return "This photo couldn't be sent to the monsters. Try retaking it.";
    case "session_expired": return "You've been signed out. Sign in again, then try again.";
    case "network": return "Couldn't reach the Monster Marks server. Check your internet connection and try again.";
    case "server_misconfigured": return "The server's AI key or model setting isn't working. Whoever set up Monster Marks needs to check it.";
    default: return "Something went wrong reaching the monsters. Try again.";
  }
}
const errorCodeText = (e: unknown): string => codeOf(e) || (e instanceof Error ? e.message : String(e));

/* ---------------- grading pipeline ---------------- */
async function addPhotos(files: FileList | null): Promise<void> {
  const list = Array.from(files || []); if (!list.length) return;
  const added: [Page, File][] = [];
  for (const f of list) { const p: Page = { id: newId(), n: ++S.pageCounter, status: "loading", corners: [] }; S.pages.push(p); added.push([p, f]); }
  S.cur = added[0][0].id; renderGrade();
  for (const [p, f] of added) {
    try {
      const src = await loadImageFile(f);
      p.src = src; p.thumb = scaleCanvas(src, 160).toDataURL("image/jpeg", 0.6);
      const target = p as Page & CropTarget;
      await autoCorners(target);
      // Students skip straightening: a page whose edges weren't found is used as taken.
      if (S.user?.student || (S.autoStraighten && p.detected)) await straighten(target, !p.detected); else p.status = "straighten";
    }
    catch (e) { p.status = "error"; p.error = e instanceof Error ? e.message : String(e); p.retry = null; }
    if (p.id === S.cur) renderGrade(); else renderStack();
  }
}
async function autoCorners(obj: CropTarget): Promise<void> {
  await new Promise(r => setTimeout(r, 0)); // let the UI paint between pages
  const det = detectPage(obj.src);
  obj.detected = det ? det.map((c): Pt => [c[0], c[1]]) : null;
  obj.corners = det ? det.map((c): Pt => [c[0], c[1]]) : fullCorners(obj.src);
  obj.auto = !!det;
}
async function straighten(p: Page & CropTarget, whole: boolean): Promise<void> {
  if (whole) { p.corners = fullCorners(p.src); p.auto = false; }
  await processCrop(p); p.status = "ready";
}
/** A page that has a photo loaded (every page past "loading"). */
const withPhoto = (p: Page): Page & CropTarget => {
  if (!p.src) throw new Error("This page has no photo.");
  return p as Page & CropTarget;
};

type ReadOutcome = "ok" | "cancelled" | "blocked" | ErrorCode | "error";
async function runRead(p: Page): Promise<ReadOutcome> {
  if (!S.aiReady) return "blocked";
  const ctl = new AbortController();
  // The server reads students' pages with the standard model whatever we ask, so name Fiona and her time for them.
  p.status = "reading"; p.ctl = ctl; p.readBy = S.user?.student ? "default" : S.tier; update(p);
  try {
    const choice = S.stackWs, ws = choice !== "auto" && choice !== "plain" ? wsById(choice) : null;
    const template = ws?.templateImageId ? await getImage(ws.templateImageId) : null;
    const library = choice === "auto" && S.worksheets.length ? S.worksheets.map(w => wsForServer(w) as WorksheetForAI) : null;
    const r = await ai("read", { image: p.sendUrl, template, worksheet: wsForServer(ws), library, careful: p.readBy === "complex" }, ctl.signal);
    p.extraction = normalizeExtraction(r);
    p.studentName = str(r.student_name);
    p.matchNote = "";
    const matched = choice === "auto" && r.worksheet_id ? wsById(str(r.worksheet_id)) : null;
    if (ws) p.worksheetId = ws.id;
    else if (matched && r.match_confidence !== "none") {
      p.worksheetId = matched.id;
      p.matchNote = r.match_confidence === "high" ? "" : `This looks like "${matched.title}", but ${MONSTERS[p.readBy].name} wasn't sure. Change it below if that's wrong.`;
    } else p.worksheetId = null;
    // Students go straight on to grading, without checking the reading first.
    if (S.user?.student) await runCheck(p); else { p.status = "review"; update(p); }
    return "ok";
  } catch (e) {
    const code = codeOf(e);
    if (code === "cancelled") { p.status = "ready"; update(p); return "cancelled"; }
    p.status = "error"; p.error = errorMessage(code); p.errorCode = errorCodeText(e); p.retry = "ready"; update(p);
    return code || "error";
  }
}
async function runCheck(p: Page): Promise<void> {
  const ex = p.extraction; if (!ex) return;
  const items = ex.items.filter(it => it.label || it.prompt || it.response);
  // A student's page arrives here still "reading", so show the review screen to let them add answers.
  if (!items.length) { p.status = "review"; update(p); toast("Add at least one answer before grading."); return; }
  ex.items = items;
  items.forEach(syncBlank);
  const ws = wsById(p.worksheetId);
  const ctl = new AbortController();
  p.status = "checking"; p.ctl = ctl; update(p);
  try {
    const r = await ai("check", { items, worksheet: wsForServer(ws) }, ctl.signal);
    const graded = mergeCheck(items, r, ws);
    const sub: Submission = {
      id: p.sub?.id || newId(), createdAt: p.sub?.createdAt || Date.now(), imageId: p.sub?.imageId,
      studentName: p.studentName || "", worksheetId: ws?.id || null,
      worksheetTitle: ws?.title || ex.printedTitle || "Free form", gradeLevel: ws?.grade || "",
      items: graded, summary: str(r.summary), patterns: strList(r.patterns, 3), next_step: str(r.next_step),
      gradedAt: Date.now(), score: 0, possible: 0, ...(p.readBy ? { careful: p.readBy === "complex" } : {}),
    };
    recompute(sub);
    const existing = S.submissions.findIndex(x => x.id === sub.id);
    if (existing >= 0) S.submissions[existing] = sub;
    p.sub = sub; p.status = "done"; update(p);
    await saveSubmission(sub, p); renderHeaderCount();
  } catch (e) {
    const code = codeOf(e);
    if (code === "cancelled") { p.status = "review"; update(p); return; }
    p.status = "error"; p.error = errorMessage(code); p.errorCode = errorCodeText(e); p.retry = "review"; update(p);
  }
}
async function readAll(): Promise<void> {
  S.batch = true; S.stopBatch = false; renderStack();
  for (const p of S.pages) {
    if (S.stopBatch) break;
    if (p.status !== "ready") continue;
    const res = await runRead(p);
    if (res === "blocked" || res === "rate_limited") break;
  }
  S.batch = false; S.stopBatch = false; renderStack();
}
function update(p: Page): void {
  if (S.tab !== "grade") return;
  if (p.id === S.cur) { renderStack(); renderWork(); } else renderStack();
}

/* ---------------- rendering: shell ---------------- */
// Busy-state checkmark, stroked with the brand gradient (stops come from CSS vars, so it follows the theme)
const PENCIL = `<svg class="pencil" viewBox="0 0 64 64" aria-hidden="true"><defs><linearGradient id="pencil-grad" x1="0" y1="0" x2="1" y2="0"><stop offset="0" class="pencil-start"/><stop offset="1" class="pencil-end"/></linearGradient></defs><path d="M10 34 C16 38 20 44 24 52 C32 36 42 22 56 12"/></svg>`;
const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true"><path d="M5 17 C8 19 10 22 12 25 C16 17 21 10 28 5" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
// Only the current tab gets the underline element, so the view transition slides it from the old tab to the new one
const tabButton = (tab: Tab, label: string): string => S.tab === tab
  ? `<button data-tab="${tab}" aria-current="page"><span class="tab-bar" aria-hidden="true"></span>${label}</button>`
  : `<button data-tab="${tab}">${label}</button>`;
function render(): void {
  if (S.ready && !S.user) return renderAuth();
  const banners: string[] = [];
  $("#app").innerHTML = `
    <header class="top">
      <div class="brand"><span class="logo">${LOGO}Monster Marks</span><span class="tagline">${TAGLINE}</span></div>
      <nav class="tabs" aria-label="Sections">
        ${tabButton("grade", "Grade")}
        ${S.user?.student ? "" : tabButton("sheets", "Worksheets")}
        ${tabButton("results", `Results <span class="count" id="rcount">${S.submissions.length || ""}</span>`)}
      </nav>
      ${S.user ? `<button class="btn quiet" id="signout" title="${esc(S.user.email)}">Sign out</button>` : ""}
    </header>
    ${banners.map(b => `<div class="banner-wrap"><div class="banner">${esc(b)}</div></div>`).join("")}
    <main id="main"></main>`;
  $$(".tabs button").forEach(b => (b.onclick = () => {
    const tab = b.dataset.tab as Tab, order: Tab[] = ["grade", "sheets", "results"];
    if (tab === S.tab) return;
    document.documentElement.dataset.dir = order.indexOf(tab) < order.indexOf(S.tab) ? "back" : "fwd";
    S.tab = tab; document.startViewTransition ? document.startViewTransition(render) : render();
  }));
  const so = $("#signout"); if (so) so.onclick = async () => { try { await api("POST", "/api/logout"); } catch (e) {}
    if (!matchMedia("(prefers-reduced-motion: reduce)").matches) await document.documentElement.animate({ opacity: [1, 0] }, { duration: 200, fill: "forwards" }).finished;
    location.reload(); };
  if (!S.ready) { $("#main").innerHTML = `<div class="busy"><p class="muted">Opening your markbook…</p></div>`; return; }
  if (S.tab === "grade") renderGrade(); else if (S.tab === "sheets") renderSheets(); else renderResults();
}
function renderHeaderCount(): void { const c = $("#rcount"); if (c) c.textContent = S.submissions.length ? String(S.submissions.length) : ""; }
const canRead = (): boolean => S.aiReady;
function renderAuth(): void {
  const signup = S.authMode === "signup", student = S.authMode === "student";
  $("#app").innerHTML = `<main class="auth">
    <div class="panel auth-card">
      ${peeker("pointer")}
      <span class="monster winnie" data-monster="winnie" aria-hidden="true"></span>
      <div class="auth-brand"><span class="logo">${LOGO}Monster Marks</span><span class="tagline">${TAGLINE}</span></div>
      <h2>${signup ? "Create your account" : student ? "Student sign in" : "Sign in"}</h2>
      <form id="authForm" class="auth-form" novalidate>
        <label class="field"><span>${student ? "Your name" : "Username or email"}</span><input name="email" type="text" ${student ? `placeholder="arogers" aria-describedby="nameHint"` : ""} autocomplete="username" autocapitalize="none" spellcheck="false" required>${student ? `<small class="hint" id="nameHint">First letter of your first name, then your last name. Alisa Rogers types <b>arogers</b>.</small>` : ""}</label>
        ${student ? "" : `<label class="field"><span>Password</span><input name="password" type="password" autocomplete="${signup ? "new-password" : "current-password"}" minlength="8" required></label>`}
        ${signup ? `<label class="field"><span>Invite code</span><input name="code" autocomplete="off" required></label>` : ""}
        <p class="auth-err" id="authErr" role="alert"></p>
        <button class="btn primary" type="submit">${signup ? "Create account" : "Sign in"}</button>
      </form>
      <div class="auth-links">
        <button class="linkbtn" id="authSwitch">${student ? "Teacher? Sign in" : signup ? "Already have an account? Sign in" : "New here? Create an account with an invite code"}</button>
        ${student ? "" : `<button class="linkbtn" id="studentSwitch">I'm a student</button>`}
      </div>
    </div></main>`;
  $("#authSwitch").onclick = () => { S.authMode = signup || student ? "login" : "signup"; renderAuth(); };
  const ss = $("#studentSwitch"); if (ss) ss.onclick = () => { S.authMode = "student"; renderAuth(); };
  $<HTMLFormElement>("#authForm").onsubmit = async e => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const f = new FormData(form), btn = $<HTMLButtonElement>("button[type=submit]", form);
    btn.disabled = true; $("#authErr").textContent = "";
    try {
      const r = await api<{ user: User }>("POST", signup ? "/api/signup" : student ? "/api/student" : "/api/login", Object.fromEntries(f));
      S.user = r.user; document.documentElement.dataset.dir = "auth";
      await (document.startViewTransition ? document.startViewTransition(startApp).finished : startApp());
    } catch (err) { $("#authErr").textContent = (err as Partial<CodedError>).serverMessage || "Couldn't reach the server. Try again."; btn.disabled = false; }
  };
  $<HTMLInputElement>("#authForm input").focus();
}

/* ---------------- rendering: grade ---------------- */
function renderGrade(): void {
  const main = $("#main"); if (!main || S.tab !== "grade") return;
  if (!$("#stack")) main.innerHTML = `<div class="grade"><aside class="stack" id="stack" aria-label="Pages in this stack"></aside><section class="work" id="work" aria-live="polite"></section></div>`;
  renderStack(); renderWork();
}
const ICON_CAMERA = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>`;
const ICON_UPLOAD = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M4 14v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5"/></svg>`;
function photoInputs(prefix: string): string {
  return `<label class="btn primary icon" title="Take photo">${ICON_CAMERA}<input type="file" accept="image/*" capture="environment" class="visually-hidden" aria-label="Take photo" data-photo="${prefix}"></label>
          <label class="btn icon" title="Upload photos">${ICON_UPLOAD}<input type="file" accept="image/*" multiple class="visually-hidden" aria-label="Upload photos" data-photo="${prefix}"></label>`;
}
const STATUS_TEXT: Record<Exclude<PageStatus, "done">, string> = { loading: "Loading…", straighten: "Place the corners", ready: "Ready to read", reading: "Reading…", review: "Check what was read", checking: "Grading…", error: "Needs attention" };
function statusText(p: Page): string {
  if (p.status === "straighten" && p.detected === null) return `<span class="st err">Couldn't find the edges</span>`;
  if (p.status === "done") return p.sub ? `<span class="st done">${fmtNum(p.sub.score)}/${fmtNum(p.sub.possible)}</span>` : "";
  const t = STATUS_TEXT[p.status];
  return `<span class="st ${p.status === "error" ? "err" : ""}">${t}</span>`;
}
function renderStack(): void {
  const el = $("#stack"); if (!el) return;
  const opts = `<option value="auto">Detect it from each page</option><option value="plain">Free form, no worksheet</option>` +
    S.worksheets.map(w => `<option value="${esc(w.id)}">${esc(w.title || "Untitled worksheet")}</option>`).join("");
  const nStraight = S.pages.filter(p => p.status === "straighten").length, nReady = S.pages.filter(p => p.status === "ready").length;
  const nDone = S.pages.filter(p => p.status === "done").length, teacher = !S.user?.student;
  el.innerHTML = `
    ${teacher ? `<label class="field"><span>Worksheet for this stack</span><select id="stackWs">${opts}</select></label>` : ""}
    <div class="add">${photoInputs("stack")}</div>
    ${teacher ? `<label class="check"><input type="checkbox" id="autoSt" ${S.autoStraighten ? "checked" : ""}> Straighten new photos automatically</label>` : ""}
    ${S.pages.length ? `<ol class="pages">${S.pages.map(p => `<li><button class="pg" data-pg="${p.id}" aria-current="${p.id === S.cur}">
        ${p.thumb ? `<img src="${p.thumb}" alt="">` : `<span class="ph"></span>`}
        <span><span class="nm">${esc(p.sub?.studentName || p.studentName || `Page ${p.n}`)}</span>${statusText(p)}</span></button></li>`).join("")}</ol>` : ""}
    ${S.pages.length ? `<div class="stack-foot">
      ${nStraight ? `<button class="btn quiet" id="skipAll">Straighten ${nStraight > 1 ? `the other ${nStraight} pages` : "the remaining page"} as placed</button>` : ""}
      ${S.batch ? (teacher ? `<button class="btn" id="stopAll">Stop after this page</button>` : "")
        : nReady && canRead() ? `<button class="btn primary" id="readAll">Read ${nReady > 1 ? `all ${nReady} pages` : "1 page"}</button>` : ""}
      ${canRead() && teacher ? `<label class="field"><span class="reader">${monsterImg(S.tier)}Who reads the handwriting</span><select id="tier"><option value="default">${STANDARD} (faster)</option><option value="complex">${CAREFUL} (for messy writing)</option></select></label>` : ""}
      ${nDone ? `<button class="btn quiet" id="clearDone">Clear ${nDone} graded page${nDone > 1 ? "s" : ""} from the stack</button>` : ""}
    </div>` : ""}`;
  const sw = $<HTMLSelectElement>("#stackWs");
  if (sw) { sw.value = S.worksheets.some(w => w.id === S.stackWs) || S.stackWs === "plain" ? S.stackWs : "auto"; sw.onchange = e => (S.stackWs = val(e)); }
  const ast = $<HTMLInputElement>("#autoSt"); if (ast) ast.onchange = e => (S.autoStraighten = (e.target as HTMLInputElement).checked);
  const tier = $<HTMLSelectElement>("#tier"); if (tier) { tier.value = S.tier; tier.onchange = e => { S.tier = val(e) as Tier; renderStack(); if (S.pages.find(x => x.id === S.cur)?.status === "ready") renderWork(); }; }
  $$<HTMLInputElement>("[data-photo]", el).forEach(inp => (inp.onchange = e => { void addPhotos((e.target as HTMLInputElement).files); (e.target as HTMLInputElement).value = ""; }));
  $$("[data-pg]", el).forEach(b => (b.onclick = () => { S.cur = b.dataset.pg || null; renderStack(); renderWork(); if (innerWidth < 860) $("#work").scrollIntoView({ behavior: "smooth" }); }));
  const skip = $<HTMLButtonElement>("#skipAll"); if (skip) skip.onclick = async () => { skip.disabled = true; for (const p of S.pages.filter(p => p.status === "straighten")) await straighten(withPhoto(p), false); renderStack(); renderWork(); };
  const ra = $("#readAll"); if (ra) ra.onclick = () => { void readAll(); };
  const sa = $<HTMLButtonElement>("#stopAll"); if (sa) sa.onclick = () => { S.stopBatch = true; sa.disabled = true; sa.textContent = "Stopping…"; };
  const cd = $("#clearDone"); if (cd) cd.onclick = () => { S.pages = S.pages.filter(p => p.status !== "done"); if (!S.pages.some(p => p.id === S.cur)) S.cur = S.pages[0]?.id || null; renderStack(); renderWork(); };
}
function renderWork(): void {
  const el = $("#work"); if (!el) return;
  const p = S.pages.find(x => x.id === S.cur);
  if (!p) {
    el.innerHTML = `<div class="empty">
      ${S.user?.student ? `<div class="duo">${monsterImg("default")}</div>
      <h2>Check your work</h2>
      <p class="hint">Take a photo of your worksheet. ${STANDARD} reads what you wrote and marks every answer.</p>` : `<div class="duo">${monsterImg("complex")}${monsterImg("default")}</div>
      <h2>Start a stack</h2>
      <p class="hint">Photograph each student's page, or upload a batch of photos. Your ${HELPER} reads what the student wrote, checks it against your answer key, and marks it up for you to review.</p>`}
      ${S.user?.student ? "" : `<p class="hint">${STANDARD} reads neat handwriting quickly. ${CAREFUL} takes longer but untangles messy writing. Once photos are in the stack, pick who reads them.</p>`}
      <div class="actions">${photoInputs("empty")}</div>
      ${S.worksheets.length || S.user?.student ? "" : `<p class="hint">Grading the same worksheet often? Add its blank copy under Worksheets first, so answers are checked against your key.</p>`}
    </div>`;
    $$<HTMLInputElement>("[data-photo]", el).forEach(inp => (inp.onchange = e => { void addPhotos((e.target as HTMLInputElement).files); (e.target as HTMLInputElement).value = ""; }));
    return;
  }
  const views: Record<PageStatus, (el: HTMLElement, p: Page) => void> = {
    loading: workBusy, straighten: workStraighten, ready: workReady, reading: workBusy, checking: workBusy, review: workReview, done: workDone, error: workError,
  };
  views[p.status](el, p);
}
/** What the monster is busy doing, in gradient text over a hand-drawn underline. */
const verb = (word: string): string => `<span class="verb">${word}</span>`;
function workBusy(el: HTMLElement, p: Page): void {
  const by: Tier = p.status === "reading" ? p.readBy || S.tier : "default";
  // Loading the photo shows just the pencil; its label is only for screen readers.
  const msg = p.status === "loading" ? `<p class="visually-hidden" role="status">Loading the photo…</p>` : `<p>${p.status === "reading" ? `${MONSTERS[by].name} is ${verb("reading")} the student's handwriting. This usually takes ${MONSTERS[by].readTime}.` : `${STANDARD} is ${verb("grading")} each answer. This usually takes ${GRADE_TIME}.`}</p>`;
  const quip = p.status === "loading" ? "" : quipLine(p.status === "reading" ? "reading" : "checking", MONSTERS[by].name);
  el.innerHTML = `<div class="busy">${p.status === "loading" ? PENCIL : workingMonster(by, p.status === "reading" ? "reading" : "grading")}${quip}${msg}
    ${p.ctl && !S.user?.student ? `<button class="btn quiet" id="stop">Stop</button>` : ""}</div>`;
  const s = $("#stop"); if (s) s.onclick = () => p.ctl?.abort();
}
function pageNav(_p: Page): string { return `<button class="btn quiet remove" data-remove>Remove page</button>`; }
function wireRemove(el: HTMLElement, p: Page): void {
  const b = el.querySelector<HTMLButtonElement>("[data-remove]"); if (!b) return;
  b.onclick = () => {
    if (!b.dataset.armed) { b.dataset.armed = "1"; b.textContent = "Remove this page?"; b.classList.add("danger"); return; }
    p.ctl?.abort(); const i = S.pages.indexOf(p); S.pages.splice(i, 1);
    S.cur = (S.pages[i] || S.pages[i - 1])?.id || null; renderStack(); renderWork();
  };
}
function workStraighten(el: HTMLElement, p: Page): void {
  const hint = p.detected === null ? "The page edges couldn't be found automatically, often because the desk is as light as the paper. Drag the four dots onto the corners of the paper."
    : p.detected ? "The dots were placed automatically. Drag any that are off so they sit on the paper's corners." : "";
  el.innerHTML = `<div class="panel crop">
    <h2>${p.storedUrl ? "Edit the corners" : "Line up the page"}</h2>
    <div id="cropHost" class="crop"></div>
    <div class="actions">
      <button class="btn primary" id="doCrop">Straighten page</button>
      <button class="btn" id="redetect">Find edges again</button>
      <button class="btn quiet" id="whole">Use photo as taken</button>
      <button class="btn quiet" id="rot">Rotate</button>
      ${pageNav(p)}
    </div></div>`;
  const c = withPhoto(p);
  mountCrop($("#cropHost"), c, hint);
  $("#rot").onclick = async () => { c.src = rotateCanvas(c.src); await autoCorners(c); renderStack(); renderWork(); };
  $("#redetect").onclick = async () => { await autoCorners(c); if (!p.detected) toast("Still couldn't find the edges. Place the dots by hand."); renderStack(); renderWork(); };
  const go = async (whole: boolean) => { $<HTMLButtonElement>("#doCrop").disabled = $<HTMLButtonElement>("#whole").disabled = true; await straighten(c, whole); renderStack(); renderWork(); };
  $<HTMLButtonElement>("#doCrop").onclick = () => go(false); $<HTMLButtonElement>("#whole").onclick = () => go(true);
  wireRemove(el, p);
}
function workReady(el: HTMLElement, p: Page): void {
  el.innerHTML = `<div class="split">
    <figure class="page-img"><img src="${p.storedUrl}" alt="Straightened photo of the page"></figure>
    <div class="panel review-form">
      <h2>Ready to read</h2>
      ${p.auto && !S.user?.student ? `<p class="match">Straightened automatically. If part of the page is cut off or the desk shows, edit the corners.</p>` : ""}
      <div class="reader">${monsterImg(S.tier)}<p class="hint">${MONSTERS[S.tier].name} will pull out only what the student wrote, keeping every mistake so they can be marked.</p></div>
      <div class="actions">
        ${canRead() ? `<button class="btn primary" id="read">${S.user?.student ? "Read and grade" : "Read this page"}</button>` : `<button class="btn primary" id="typeIn">Type the answers in</button>`}
        ${S.user?.student ? "" : `<button class="btn" id="recrop">Edit corners</button>`}
        ${canRead() ? `<button class="btn quiet" id="typeIn">Type the answers in</button>` : ""}
        ${pageNav(p)}
      </div></div></div>`;
  const rd = $("#read"); if (rd) rd.onclick = () => { void runRead(p); };
  const rc = $("#recrop"); if (rc) rc.onclick = () => { p.status = "straighten"; renderStack(); renderWork(); };
  $("#typeIn").onclick = () => {
    p.worksheetId = S.stackWs !== "auto" && S.stackWs !== "plain" ? S.stackWs : null;
    const ws = wsById(p.worksheetId);
    p.extraction = { printedTitle: "", items: (ws?.questions || []).map((q): ReadItem => ({ label: q.label, prompt: q.prompt, response: "", work: "", confidence: "high", flags: [] })) };
    p.studentName = p.studentName || ""; p.status = "review"; renderStack(); renderWork(); };
  wireRemove(el, p);
}
function workError(el: HTMLElement, p: Page): void {
  el.innerHTML = `<div class="busy"><h2>That didn't work</h2><p>${esc(p.error)}</p>${p.errorCode ? `<p class="muted">Error code: ${esc(p.errorCode)}</p>` : ""}<div class="actions">
    ${p.retry ? `<button class="btn primary" id="retry">Try again</button>` : ""}${pageNav(p)}</div></div>`;
  const r = $("#retry"); if (r) r.onclick = () => { const to = p.retry; if (!to) return; p.status = to; if (to === "ready") void runRead(p); else if (to === "review") void runCheck(p); };
  wireRemove(el, p);
}
function wsOptions(sel: string | null | undefined): string {
  return `<option value="">Free form (${STANDARD} works out the answers)</option>` +
    S.worksheets.map(w => `<option value="${esc(w.id)}" ${w.id === sel ? "selected" : ""}>${esc(w.title || "Untitled worksheet")}</option>`).join("");
}
// The grader's "blank" flag follows the answer box: on while it's empty, off once something is typed.
// It goes first so the server's five-flag cap never drops it.
function syncBlank(it: ReadItem): void {
  const rest = it.flags.filter(f => f !== "blank");
  it.flags = it.response.trim() ? rest : ["blank", ...rest];
}
// Answers whose "Add work shown" link was opened, so the empty field survives a re-render
const workOpen = new WeakSet<ReadItem>();
// Tracks the sticky review bar's height, so a focused answer scrolls clear of it however many rows it wraps to
let reviewBarObserver: ResizeObserver | null = null;
function workReview(el: HTMLElement, p: Page): void {
  const ex = p.extraction; if (!ex) return;
  const items = ex.items;
  const itemHtml = (it: ReadItem, i: number): string => `<li class="item ${it.confidence}" data-i="${i}">
    <div class="lbl"><input aria-label="Question label" data-f="label" value="${esc(it.label)}"></div>
    <div class="flds">
      <input class="prompt" data-f="prompt" aria-label="Printed question" placeholder="Question (printed on the page)" value="${esc(it.prompt)}">
      <input class="ans" data-f="response" aria-label="Student's answer" placeholder="Student's answer" value="${esc(it.response)}">
      ${it.work || workOpen.has(it) ? workInput(it) : `<button type="button" class="addwk" data-addwork>Add work shown</button>`}
      ${flagsHtml(it)}
    </div>
    <button class="x" data-del="${i}" aria-label="Remove question ${esc(it.label)}">×</button></li>`;
  const workInput = (it: ReadItem): string => `<input class="wk" data-f="work" aria-label="Work shown" placeholder="Work shown" value="${esc(it.work)}">`;
  const flagsHtml = (it: ReadItem): string => {
    // "illegible" just repeats a low confidence, so only the confidence tag is shown.
    // "blank" on an empty answer box says nothing new; it's only shown when the box isn't empty, since that contradicts it.
    const flags = it.flags.filter(f => (f !== "illegible" || it.confidence !== "low") && (f !== "blank" || it.response.trim()));
    const conf = it.confidence !== "high" ? `<span class="flag">${it.confidence === "low" ? "Hard to read" : "Double-check"}</span>` : "";
    const ok = it.confidence !== "high" ? `<button class="ok" data-ok aria-label="Answer ${esc(it.label)} looks right">Looks right</button>` : "";
    return conf || flags.length ? `<div class="flags">${conf}${flags.map(f => `<span class="flag">${esc(f.replace(/_/g, " "))}</span>`).join("")}${ok}</div>` : "";
  };
  // Marks an item's reading as checked by the teacher, so it's graded as read correctly.
  const markChecked = (li: HTMLElement, it: ReadItem): void => {
    it.confidence = "high";
    // A reading the teacher has checked is no longer illegible.
    it.flags = it.flags.filter(f => f !== "illegible");
    li.classList.remove("low", "medium"); li.classList.add("high");
    li.querySelector(".flags")?.remove();
    li.querySelector(".flds")?.insertAdjacentHTML("beforeend", flagsHtml(it));
    $("#unsure").textContent = unsureText();
    $("#nextFlag").hidden = !items.some(x => x.confidence !== "high");
  };
  const unsureText = (): string => {
    const n = items.filter(it => it.confidence !== "high").length;
    return n ? `${n} answer${n > 1 ? "s are" : " is"} highlighted because the writing was hard to read.` : "Everything was read clearly.";
  };
  const student = !!S.user?.student;
  el.innerHTML = `<div class="split">
    <figure class="page-img"><img src="${p.storedUrl}" alt="Photo of the student's page"></figure>
    <div class="panel review-form">
      <h2>Check what ${MONSTERS[p.readBy || S.tier].name} read</h2>
      ${items.length ? `<p class="hint">${student ? "Fix anything that doesn't match what you wrote, then grade." : "Fix anything misread before grading."} <span id="unsure">${unsureText()}</span></p>` : ""}
      <div class="two">
        <label class="field"><span>${student ? "Your name" : "Student"}</span><input id="stu" value="${esc(p.studentName)}" placeholder="Name"></label>
        ${student ? "" : `<label class="field"><span>Worksheet</span><select id="pws">${wsOptions(p.worksheetId)}</select></label>`}
      </div>
      ${p.matchNote ? `<p class="match">${esc(p.matchNote)}</p>` : ""}
      ${items.length ? `<ol class="items" id="items">${items.map(itemHtml).join("")}</ol>` : `<p class="hint">No answers were found on this page. Add them below, or retake the photo.</p>`}
      <div class="actions"><button class="btn quiet" id="addItem">Add a question</button>${pageNav(p)}</div>
      <div class="actions review-bar">
        <button class="btn primary" id="check" ${S.aiReady ? "" : "disabled"}>Grade</button>
        <button class="btn" id="reread">Re-read</button>
        ${items.length ? `<button type="button" class="btn quiet" id="nextFlag" aria-label="Next highlighted answer" ${items.some(x => x.confidence !== "high") ? "" : "hidden"}>Next highlight</button>` : ""}
      </div></div></div>`;
  $<HTMLInputElement>("#stu").oninput = e => { p.studentName = val(e); const nm = $(`[data-pg="${p.id}"] .nm`); if (nm) nm.textContent = val(e) || `Page ${p.n}`; };
  const pws = $<HTMLSelectElement>("#pws"); if (pws) pws.onchange = e => { p.worksheetId = val(e) || null; p.matchNote = ""; };
  const list = $("#items");
  let lastItem: HTMLElement | null = null;
  if (list) {
    list.addEventListener("input", e => {
      const li = (e.target as Element).closest<HTMLElement>("[data-i]"), f = (e.target as HTMLElement).dataset.f as "label" | "prompt" | "response" | "work" | undefined;
      if (!li || !f) return;
      const it = items[Number(li.dataset.i)];
      it[f] = val(e);
      if (f === "response") syncBlank(it);
      // An answer the teacher has edited counts as checked.
      if (f === "response" && it.confidence !== "high") markChecked(li, it);
    });
    // Remember the answer the teacher was last in: clicking or tabbing to "Next highlighted answer" moves focus onto the button.
    list.addEventListener("focusin", e => { lastItem = (e.target as Element).closest<HTMLElement>("[data-i]"); });
    list.addEventListener("click", e => {
      const b = (e.target as Element).closest<HTMLElement>("[data-del]"); if (b) { items.splice(Number(b.dataset.del), 1); renderWork(); return; }
      const aw = (e.target as Element).closest<HTMLElement>("[data-addwork]");
      if (aw) {
        const awLi = aw.closest<HTMLElement>("[data-i]"), it = awLi && items[Number(awLi.dataset.i)];
        if (awLi && it) { workOpen.add(it); aw.outerHTML = workInput(it); awLi.querySelector<HTMLInputElement>(".wk")?.focus(); }
        return;
      }
      const ok = (e.target as Element).closest<HTMLElement>("[data-ok]"), li = ok?.closest<HTMLElement>("[data-i]");
      if (li) { markChecked(li, items[Number(li.dataset.i)]); li.querySelector<HTMLInputElement>(".ans")?.focus(); }
    });
  }
  $("#addItem").onclick = () => { items.push({ label: String(items.length + 1), prompt: "", response: "", work: "", confidence: "high", flags: [] }); renderWork(); const last = $$("#items .ans").pop(); last?.focus(); };
  const nf = el.querySelector<HTMLButtonElement>("#nextFlag");
  if (nf) nf.onclick = () => {
    // Cycle through the highlighted answers, starting after the one the teacher was last in (jumping there focuses it too).
    const flagged = $$<HTMLElement>("#items .item.low, #items .item.medium"); if (!flagged.length) return;
    // Document order, so this still works after that answer was checked and dropped out of the list.
    // If the teacher has since scrolled that answer out of view, go on from what's on screen instead.
    const onScreen = (x: Element): boolean => { const r = x.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight; };
    const from = lastItem?.isConnected && onScreen(lastItem) ? lastItem : null;
    const li = (from ? flagged.find(x => from.compareDocumentPosition(x) & Node.DOCUMENT_POSITION_FOLLOWING)
      : flagged.find(x => x.getBoundingClientRect().top >= 0)) || flagged[0];
    li.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
    li.querySelector<HTMLInputElement>(".ans")?.focus({ preventScroll: true });
  };
  $("#check").onclick = () => { void runCheck(p); };
  $("#reread").onclick = () => { void runRead(p); };
  reviewBarObserver?.disconnect(); reviewBarObserver = null;
  const bar = el.querySelector<HTMLElement>(".review-bar");
  if (bar && typeof ResizeObserver !== "undefined") {
    reviewBarObserver = new ResizeObserver(() => document.documentElement.style.setProperty("--review-bar-h", `${bar.offsetHeight}px`));
    reviewBarObserver.observe(bar);
  }
  wireRemove(el, p);
}
function workDone(el: HTMLElement, p: Page): void {
  el.innerHTML = `<div class="panel"><div id="detailHost"></div>
    <div class="actions mt-4">
      <button class="btn quiet" id="next">Next page</button>
      <button class="btn quiet" id="edit">Edit</button>
    </div></div>`;
  if (!p.sub) return;
  renderDetail($("#detailHost"), p.sub, { imageUrl: p.storedUrl, onName: v => { p.studentName = v; renderStack(); } });
  const nextP = S.pages.slice(S.pages.indexOf(p) + 1).concat(S.pages.slice(0, S.pages.indexOf(p))).find(x => x.status !== "done");
  const nb = $("#next");
  if (nextP) nb.onclick = () => { S.cur = nextP.id; renderStack(); renderWork(); };
  else { nb.textContent = "Add pages"; nb.onclick = () => { S.cur = null; renderStack(); renderWork(); }; }
  $("#edit").onclick = () => { p.status = "review"; renderStack(); renderWork(); };
}

/* ---------------- detail (marked page) ---------------- */
function markSvg(v: Verdict): string {
  if (v === "correct") return `<svg viewBox="0 0 44 44" aria-hidden="true"><path d="M8 24 C12 27 15 31 18 36 C23 25 30 15 38 8" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  if (v === "incorrect") return `<svg viewBox="0 0 44 44" aria-hidden="true"><path d="M10 10 C18 19 26 27 35 36 M34 9 C26 18 18 27 9 35" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>`;
  return `<span class="hm" aria-hidden="true">${v === "partial" ? "½" : v === "blank" ? "–" : "?"}</span>`;
}
const VERDICTS: readonly [Verdict, string][] = [["correct", "Correct"], ["partial", "Partly right"], ["incorrect", "Incorrect"], ["blank", "Left blank"], ["unclear", "Can't tell"]];
interface DetailOptions { imageUrl?: string; onName?: (name: string) => void }
function renderDetail(host: HTMLElement, sub: Submission, opts: DetailOptions = {}): void {
  const pct = sub.possible ? Math.round((sub.score / sub.possible) * 100) : 0;
  const reader = readerOf(sub), hasNote = !!(sub.summary || sub.patterns?.length || sub.next_step);
  const hideX = !!S.user?.student; // students don't see red Xs on their wrong answers
  host.innerHTML = `<div class="detail">
    <div class="detail-top">
      <div class="who">
        <label class="field"><span>Student</span><input id="dStu" value="${esc(sub.studentName)}" placeholder="Name"></label>
        <p class="muted">${esc(sub.worksheetTitle)}${sub.gradeLevel ? `, grade ${esc(sub.gradeLevel)}` : ""}<br>Graded ${fmtDate(sub.gradedAt || sub.createdAt)}</p>
        ${reader && !hasNote ? `<p class="muted read-by">Read by ${readerTag(sub)}</p>` : ""}
      </div>
      <div class="score" id="score" aria-live="polite">
        <svg viewBox="0 0 170 96" preserveAspectRatio="none" aria-hidden="true"><path d="M30 20 C70 4 150 8 160 44 C168 78 90 94 44 86 C8 80 4 44 34 26 C50 16 72 12 96 14"/></svg>
        <span class="score-num"><span id="sNum">${fmtNum(sub.score)}</span><small>/${fmtNum(sub.possible)}</small></span><span class="pct" id="sPct">${pct}%</span>
      </div>
    </div>
    ${hasNote ? `<div class="note-row">
      ${reader ? `<figure class="note-monster">${monsterImg(reader)}<figcaption>Read by ${MONSTERS[reader].name}</figcaption></figure>` : ""}
      <div class="margin-note">
      ${sub.summary ? `<p class="hand">${esc(sub.summary)}</p>` : ""}
      ${sub.patterns?.length ? `<div><strong>Patterns</strong><ul>${sub.patterns.map(x => `<li>${esc(x)}</li>`).join("")}</ul></div>` : ""}
      ${sub.next_step ? `<p><strong>Next step:</strong> ${esc(sub.next_step)}</p>` : ""}
    </div></div>` : ""}
    <ol class="marks" id="marks">${sub.items.map((it, i) => `<li class="mrow v-${it.verdict}" data-i="${i}">
      ${hideX && it.verdict === "incorrect" ? `<div class="mark"></div>` : `<div class="mark" title="${esc(VERDICTS.find(v => v[0] === it.verdict)?.[1])}">${markSvg(it.verdict)}</div>`}
      <div>
        <p class="q"><b>${esc(it.label)}</b>${esc(it.prompt || "No printed question")}</p>
        <p class="said ${it.response ? "" : "blank"}">${it.response ? esc(it.response) : "Nothing written"}</p>
        ${it.work ? `<p class="wshown">Work shown: ${esc(it.work)}</p>` : ""}
        ${it.expected && it.verdict !== "correct" ? `<p class="exp">Expected <b>${esc(it.expected)}</b></p>` : ""}
        ${it.note ? `<p class="note">${esc(it.note)}</p>` : ""}
      </div>
      <div class="mctl">
        <select data-v="${i}" aria-label="Mark for question ${esc(it.label)}">${VERDICTS.map(([v, l]) => `<option value="${v}" ${v === it.verdict ? "selected" : ""}>${l}</option>`).join("")}</select>
        <label class="pts"><input type="number" min="0" max="${it.points_possible}" step="0.5" data-p="${i}" value="${it.points_awarded}" aria-label="Points for question ${esc(it.label)}"> of ${fmtNum(it.points_possible)}</label>
      </div></li>`).join("")}</ol>
    <details class="photo"><summary>Show the photo</summary><img id="dImg" alt="Photo of the student's page"></details>
  </div>`;
  const refreshScore = () => { recompute(sub); $("#sNum", host).textContent = fmtNum(sub.score); $("#sPct", host).textContent = (sub.possible ? Math.round((sub.score / sub.possible) * 100) : 0) + "%"; renderStack(); };
  $("#dStu", host).oninput = e => { sub.studentName = val(e); opts.onName?.(val(e)); saveSubSoon(sub); };
  $("#marks", host).addEventListener("change", e => {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    if (t.dataset.v != null) {
      const it = sub.items[Number(t.dataset.v)]; const v = t.value as Verdict; it.verdict = v;
      it.points_awarded = v === "correct" ? it.points_possible : v === "partial" ? it.points_possible / 2 : 0;
      sub.overridden = true; recompute(sub); void saveSubmission(sub); renderDetail(host, sub, opts);
    } else if (t.dataset.p != null) {
      const it = sub.items[Number(t.dataset.p)]; it.points_awarded = Math.max(0, Math.min(it.points_possible, num(t.value)));
      t.value = String(it.points_awarded); sub.overridden = true; refreshScore(); saveSubSoon(sub);
    }
  });
  $("details.photo", host).addEventListener("toggle", async e => {
    if (!(e.target as HTMLDetailsElement).open) return; const img = $<HTMLImageElement>("#dImg", host); if (img.getAttribute("src")) return;
    const u = opts.imageUrl || (await getImage(sub.imageId)); if (u) img.src = u; else img.alt = "The photo isn't available.";
  });
}

/* ---------------- rendering: worksheets ---------------- */
function renderSheets(): void {
  const main = $("#main");
  if (S.wsEdit) return renderSheetEditor(main, S.wsEdit);
  main.innerHTML = `<div class="rhead"><div><h2>Worksheets</h2><p class="hint">Add the blank copy of a worksheet once. ${STANDARD} reads its questions and works out an answer key you can correct. Then pick it when grading a stack, or let Monster Marks detect it from each page.</p></div>
    <button class="btn primary" id="newWs">Add worksheet</button></div>
    ${S.worksheets.length ? `<ul class="ws-list">${S.worksheets.map(w => `<li><button class="ws-card" data-ws="${esc(w.id)}">
      ${w.templateImageId ? `<img data-img-id="${esc(w.templateImageId)}" alt="">` : `<span class="ph"></span>`}
      <span><strong>${esc(w.title || "Untitled worksheet")}</strong><br><span class="muted">${w.grade ? `Grade ${esc(w.grade)}, ` : ""}${(w.questions || []).length} questions</span></span></button></li>`).join("")}</ul>`
      : `<div class="panel mt-4"><p class="hint">No worksheets yet. Without one, Monster Marks still grades free-form pages and works out the answers itself.</p></div>`}`;
  $("#newWs").onclick = () => { S.wsEdit = { id: newId(), title: "", grade: "", notes: "", questions: [], stage: "photo", isNew: true }; renderSheets(); };
  $$("[data-ws]").forEach(b => (b.onclick = () => { const w = wsById(b.dataset.ws); if (!w) return; S.wsEdit = { ...plain(w), stage: "form" }; renderSheets(); }));
  $$<HTMLImageElement>("[data-img-id]").forEach(async img => { const u = await getImage(img.dataset.imgId); if (u) img.src = u; });
}
const GRADES = ["K", "1", "2", "3", "4", "5"];
async function readBlank(d: WorksheetDraft): Promise<void> {
  if (!d.src || !d.corners) return;
  const target = d as WorksheetDraft & CropTarget;
  d.stage = "reading"; d.proposed = undefined; renderSheets();
  await processCrop(target); d.templateUrl = d.storedUrl; d.zoom = undefined;
  if (!canRead()) { d.stage = "form"; d.error = `${STANDARD} can't read photos in this view, so type the questions in yourself.`; renderSheets(); return; }
  const ctl = new AbortController(); d.ctl = ctl;
  try {
    const r = await ai("blank", { image: d.sendUrl }, ctl.signal);
    d.title = d.title || str(r.title); d.grade = d.grade || (GRADES.includes(str(r.grade)) ? str(r.grade) : "");
    const read = (Array.isArray(r.questions) ? r.questions : []).map((raw: unknown, i): Question => {
      const q = asRecord(raw);
      return { label: str(q.label || i + 1), prompt: str(q.prompt), answer: str(q.answer), points: num(q.points, 1) || 1 };
    });
    // Never overwrite an answer key the teacher already has; offer the new reading instead.
    const hasKey = d.questions.some(q => q.prompt || q.answer);
    if (hasKey && read.length) d.proposed = read; else if (read.length) d.questions = read;
    d.error = read.length ? "" : "No questions were found in the photo." + (hasKey ? " Your answer key is unchanged." : " Add them below.");
  } catch (e) {
    const code = codeOf(e);
    d.error = code === "cancelled" ? "" : errorMessage(code) + " You can also type the questions in yourself.";
  }
  d.stage = "form"; if (S.tab === "sheets" && S.wsEdit === d) renderSheets();
}
function renderSheetEditor(main: HTMLElement, d: WorksheetDraft): void {
  const cancel = `<button class="btn quiet" id="wsCancel">Cancel</button>`;
  if (d.stage === "photo") {
    main.innerHTML = `<div class="panel panel-narrow">
      <h2>Add a worksheet</h2>
      <p class="hint">Photograph a blank copy, before any student has written on it. ${STANDARD} reads the questions and works out the answers for you to check.</p>
      <div class="actions">${photoInputs("ws")}</div>
      <div class="actions"><button class="btn quiet" id="manual">Type the questions in instead</button>${cancel}</div></div>`;
    $$<HTMLInputElement>("[data-photo]", main).forEach(inp => (inp.onchange = async e => {
      const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
      try {
        const src = await loadImageFile(f);
        const target: WorksheetDraft & CropTarget = Object.assign(d, { src, corners: fullCorners(src) });
        await autoCorners(target); d.stage = "crop";
      } catch (err) { toast(err instanceof Error ? err.message : String(err)); }
      renderSheets();
    }));
    $("#manual").onclick = () => { d.stage = "form"; renderSheets(); };
  } else if (d.stage === "crop") {
    if (!d.src || !d.corners) { d.stage = "photo"; return renderSheetEditor(main, d); }
    const c = d as WorksheetDraft & CropTarget;
    main.innerHTML = `<div class="panel crop"><h2>Line up the blank worksheet</h2><div id="cropHost" class="crop"></div>
      <div class="actions"><button class="btn primary" id="go">Straighten and read questions</button><button class="btn" id="whole">Use photo as taken</button><button class="btn quiet" id="rot">Rotate</button>${cancel}</div></div>`;
    mountCrop($("#cropHost"), c, d.detected ? "The dots were placed automatically. Drag any that are off so they sit on the paper's corners." : "The page edges couldn't be found automatically. Drag the four dots onto the corners of the paper.");
    $("#rot").onclick = async () => { c.src = rotateCanvas(c.src); await autoCorners(c); renderSheets(); };
    $("#go").onclick = () => { void readBlank(d); };
    $<HTMLButtonElement>("#whole").onclick = () => { c.corners = fullCorners(c.src); void readBlank(d); };
  } else if (d.stage === "reading") {
    main.innerHTML = `<div class="work"><div class="busy">${workingMonster("default", "reading")}${quipLine("key", STANDARD)}<p>${STANDARD} is ${verb("reading")} the questions and working out an answer key…</p><button class="btn quiet" id="stop">Stop</button></div></div>`;
    $("#stop").onclick = () => d.ctl?.abort();
  } else {
    const qrow = (q: Question, i: number): string => `<div class="qrow" data-i="${i}">
      <input data-f="label" aria-label="Label" value="${esc(q.label)}">
      <input data-f="prompt" aria-label="Question" placeholder="Question" value="${esc(q.prompt)}">
      <input class="qa" data-f="answer" aria-label="Correct answer" placeholder="Answer" value="${esc(q.answer)}">
      <input class="qp" data-f="points" type="number" min="0" step="0.5" aria-label="Points" value="${esc(q.points)}">
      <button class="x" data-del="${i}" aria-label="Remove question ${esc(q.label)}">×</button></div>`;
    const hasPhoto = !!(d.templateUrl || d.templateImageId);
    main.innerHTML = `${hasPhoto ? `<div class="split"><figure class="page-img" id="tmpl"></figure>` : ""}<div class="panel panel-col">
      <div class="rhead flush"><h2>${d.isNew ? "New worksheet" : "Edit worksheet"}</h2></div>
      ${d.error ? `<p class="match">${esc(d.error)}</p>` : ""}
      ${d.proposed ? `<div class="match"><p>${STANDARD} read ${d.proposed.length} question${d.proposed.length > 1 ? "s" : ""} from the new photo. The new photo will be saved; your answer key below hasn't changed.</p>
        <div class="actions"><button class="btn" id="useProposed">Replace my answer key with these</button><button class="btn quiet" id="keepKey">Keep my answer key</button></div></div>` : ""}
      <div class="two">
        <label class="field"><span>Title</span><input id="wT" value="${esc(d.title)}" placeholder="Adding within 20"></label>
        <label class="field"><span>Grade</span><select id="wG"><option value="">Not set</option>${["K", "1", "2", "3", "4", "5"].map(g => `<option ${g === d.grade ? "selected" : ""}>${g}</option>`).join("")}</select></label>
      </div>
      <label class="field"><span>Grading notes for ${STANDARD} (optional)</span><textarea id="wN" placeholder="Example: Accept answers without units. Number 6 needs the fraction in simplest form.">${esc(d.notes)}</textarea></label>
      <div><h3>Questions and answer key</h3><p class="hint">Check each answer. ${STANDARD} grades against these.</p></div>
      <div class="qtable" id="qt"><div class="qrow head"><span>No.</span><span>Question</span><span>Answer</span><span>Points</span><span></span></div>${d.questions.map(qrow).join("")}</div>
      <div class="actions"><button class="btn quiet" id="addQ">Add a question</button></div>
      <div class="actions"><button class="btn primary" id="wSave">Save worksheet</button>${cancel}
        ${d.src || d.templateImageId ? `<button class="btn quiet" id="rephoto">Replace photo</button>` : `<button class="btn quiet" id="rephoto">Add a photo</button>`}
        ${d.isNew ? "" : `<button class="btn danger" id="wDel">Delete worksheet</button>`}</div></div>${hasPhoto ? "</div>" : ""}`;
    const t = $("#tmpl");
    if (t) {
      const zoom = (d.zoom ??= newZoom()), show = (u: string) => mountZoom(t, u, "Blank worksheet", zoom);
      if (d.templateUrl) show(d.templateUrl); else void getImage(d.templateImageId).then(u => { if (u && t.isConnected) show(u); });
    }
    $("#wT").oninput = e => (d.title = val(e));
    $("#wG").onchange = e => (d.grade = val(e));
    $("#wN").oninput = e => (d.notes = val(e));
    const qt = $("#qt");
    qt.addEventListener("input", e => {
      const r = (e.target as Element).closest<HTMLElement>("[data-i]"), f = (e.target as HTMLElement).dataset.f as keyof Question | undefined;
      if (!r || !f) return;
      const q = d.questions[Number(r.dataset.i)];
      if (f === "points") q.points = num(val(e), 1); else q[f] = val(e);
    });
    qt.addEventListener("click", e => { const b = (e.target as Element).closest<HTMLElement>("[data-del]"); if (b) { d.questions.splice(Number(b.dataset.del), 1); renderSheets(); } });
    $("#addQ").onclick = () => { d.questions.push({ label: String(d.questions.length + 1), prompt: "", answer: "", points: 1 }); renderSheets(); $$("#qt [data-f='prompt']").pop()?.focus(); };
    $("#rephoto").onclick = () => { d.stage = "photo"; renderSheets(); };
    const useP = $("#useProposed"); if (useP) useP.onclick = () => { if (d.proposed) d.questions = d.proposed; d.proposed = undefined; renderSheets(); };
    const keepK = $("#keepKey"); if (keepK) keepK.onclick = () => { d.proposed = undefined; renderSheets(); };
    $("#wSave").onclick = async () => {
      if (!d.title.trim()) { toast("Give the worksheet a title."); $("#wT").focus(); return; }
      const btn = $<HTMLButtonElement>("#wSave"); btn.disabled = true; btn.textContent = "Saving…";
      try {
        if (d.templateUrl) { const old = d.templateImageId; d.templateImageId = newId(); await putImage(d.templateImageId, d.templateUrl); if (old) void delImage(old); }
        await saveWorksheetDoc({ id: d.id, title: d.title.trim(), grade: d.grade, notes: d.notes, templateImageId: d.templateImageId || null,
          questions: d.questions.filter(q => q.prompt || q.answer).map(q => ({ label: str(q.label), prompt: str(q.prompt), answer: str(q.answer), points: num(q.points, 1) })), updatedAt: Date.now() });
        S.wsEdit = null; toast("Worksheet saved."); renderSheets();
      } catch (e) { btn.disabled = false; btn.textContent = "Save worksheet"; toast("The worksheet couldn't be saved. Try again."); }
    };
    const del = $<HTMLButtonElement>("#wDel");
    if (del) del.onclick = async () => {
      if (!del.dataset.armed) { del.dataset.armed = "1"; del.textContent = "Delete for good?"; return; }
      S.worksheets = S.worksheets.filter(w => w.id !== d.id);
      if (S.base) try { await S.base.collection("worksheets").doc(d.id).delete(); } catch (e) {}
      void delImage(d.templateImageId); if (S.stackWs === d.id) S.stackWs = "auto";
      S.wsEdit = null; toast("Worksheet deleted."); renderSheets();
    };
  }
  const cancelBtn = $("#wsCancel"); if (cancelBtn) cancelBtn.onclick = () => { d.ctl?.abort(); S.wsEdit = null; renderSheets(); };
}

/* ---------------- rendering: results ---------------- */
function filteredSubs(): Submission[] { return S.submissions.filter(s => S.resultFilter === "all" || (S.resultFilter === "plain" ? !s.worksheetId : s.worksheetId === S.resultFilter)); }
// Opening a result slides in like moving to a later tab, and going back slides the other way.
function showResult(id: string | null): void {
  document.documentElement.dataset.dir = id ? "fwd" : "back";
  const go = (): void => { S.resultOpen = id; renderResults(); if (id) window.scrollTo(0, 0); };
  document.startViewTransition ? document.startViewTransition(go) : go();
}
function renderResults(): void {
  const main = $("#main");
  const open = S.resultOpen && S.submissions.find(s => s.id === S.resultOpen);
  if (open) {
    main.innerHTML = `<div class="actions mb-4"><button class="btn quiet" id="back">Back to results</button></div><div class="panel"><div id="detailHost"></div></div>`;
    renderDetail($("#detailHost"), open, {});
    $("#back").onclick = () => showResult(null);
    return;
  }
  const usedWs = [...new Set(S.submissions.map(s => s.worksheetId).filter(Boolean))];
  const list = filteredSubs();
  const avg = list.length ? Math.round(list.reduce((a, s) => a + (s.possible ? s.score / s.possible : 0), 0) / list.length * 100) : null;
  main.innerHTML = `<div class="rhead">
      <div><h2>Results</h2><p class="hint">${list.length ? `${list.length} graded page${list.length > 1 ? "s" : ""}, class average ${avg}%.` : "Graded pages show up here."}</p></div>
      <div class="actions">
        <label class="field"><span>Show</span><select id="rf"><option value="all">All worksheets</option><option value="plain">Free form</option>
          ${usedWs.map(id => `<option value="${esc(id)}">${esc(wsById(id)?.title || S.submissions.find(s => s.worksheetId === id)?.worksheetTitle || "Worksheet")}</option>`).join("")}</select></label>
        ${S.downloads && list.length ? `<button class="btn" id="csv">Export CSV</button>` : ""}
      </div></div>
    ${list.length ? `<div class="table-wrap"><table><thead><tr><th scope="col">Student</th><th scope="col">Worksheet</th><th scope="col">Score</th><th scope="col">Graded</th></tr></thead><tbody>
      ${list.map(s => `<tr><td><button class="linkbtn" data-open="${esc(s.id)}">${esc(s.studentName || "Unnamed student")}</button></td><td>${esc(s.worksheetTitle)}</td>
        <td><span class="sc">${fmtNum(s.score)}/${fmtNum(s.possible)}</span> <span class="muted">${s.possible ? Math.round(s.score / s.possible * 100) : 0}%</span></td><td>${fmtDate(s.gradedAt || s.createdAt)}</td></tr>`).join("")}
    </tbody></table></div>` : `<div class="panel"><p class="hint">Nothing graded yet. Go to Grade and photograph a student's page.</p></div>`}`;
  const rf = $<HTMLSelectElement>("#rf"); rf.value = S.resultFilter; if (rf.value !== S.resultFilter) { S.resultFilter = "all"; rf.value = "all"; }
  rf.onchange = e => { S.resultFilter = val(e); renderResults(); };
  $$("[data-open]").forEach(b => (b.onclick = () => showResult(b.dataset.open || null)));
  const csv = $("#csv"); if (csv) csv.onclick = () => { void exportCsv(); };
}
async function exportCsv(): Promise<void> {
  const list = filteredSubs();
  const single = S.resultFilter !== "all" && S.resultFilter !== "plain";
  const labels = single ? [...new Set(list.flatMap(s => s.items.map(i => i.label)))] : [];
  const head = ["Student", "Worksheet", "Read by", "Graded", "Score", "Possible", "Percent", ...labels.map(l => `Q${l}`), "Summary", "Next step"];
  const rows = list.map(s => [s.studentName, s.worksheetTitle, readerName(s), new Date(s.gradedAt || s.createdAt).toISOString().slice(0, 10), s.score, s.possible,
    s.possible ? Math.round(s.score / s.possible * 100) : 0, ...labels.map(l => s.items.find(i => i.label === l)?.points_awarded ?? ""), s.summary, s.next_step]);
  const text = [head, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob(["\ufeff" + text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = `monster-marks-results-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------------- start ---------------- */
async function startApp(): Promise<void> {
  S.aiReady = true; S.downloads = true; S.base = storage; S.ready = false; render();
  await loadAll();
  S.ready = true; render();
}
async function init(): Promise<void> {
  try { const r = await api<{ user: User }>("GET", "/api/me"); S.user = r.user; await startApp(); }
  catch (e) { S.user = null; S.ready = true; render(); }
}
mountSpotlight();
mountPeeker();
mountRoller();
// Winnie only greets teachers on the sign-in page; she doesn't read handwriting, so she isn't one of the tiers.
// Her 900x900 animation is cropped like Fiona's and Kraken's; keep in step with .monster.winnie's aspect-ratio in styles.css.
mountMonsters({ ...MONSTERS, winnie: { anim: "/monsters/winnie.json", still: "/monsters/winnie.svg", viewBox: "160 130 590 710" } });
mountQuips();
void init();
